
import type { DrugInfo } from '../types';
import { LOCAL_DRUG_DATABASE } from '../data/localDrugData';
import { pushClinicianDataToCloud } from './userSyncService';

// A simple in-memory cache for the drug database
let localDrugDatabase: DrugInfo[] | null = null;

const getUserDrugsKey = (uid: string) => `userDrugIndex_${uid}`;

/**
 * Fetches the core drug data.
 * Updated to use the TypeScript constant to ensure data availability in all environments
 * and prevent module resolution errors with JSON files.
 * @returns A promise that resolves with the array of DrugInfo.
 */
const getLocalDrugDatabase = async (): Promise<DrugInfo[]> => {
  if (localDrugDatabase) {
    return localDrugDatabase;
  }
  // Use the imported data directly
  // FUTURE TODO: Replace this with a fetch call to core.drrx.app API when ready.
  // e.g. const response = await fetch('https://api.core.drrx.app/drugs');
  localDrugDatabase = LOCAL_DRUG_DATABASE;
  return localDrugDatabase;
};

/**
 * Retrieves user-added drugs from localStorage for a specific user.
 * @param uid - The user's unique ID.
 * @returns An array of DrugInfo objects.
 */
export const getUserDrugs = (uid: string): DrugInfo[] => {
  try {
    const userDrugsJson = localStorage.getItem(getUserDrugsKey(uid));
    return userDrugsJson ? JSON.parse(userDrugsJson) : [];
  } catch (error) {
    console.error("Could not retrieve user drugs from localStorage:", error);
    return [];
  }
};

/**
 * Adds a new drug to the specific user's list in localStorage.
 * @param drug - The DrugInfo object to add.
 * @param uid - The user's unique ID.
 */
export const addUserDrug = (drug: DrugInfo, uid: string): void => {
  try {
    const userDrugs = getUserDrugs(uid);
    // Avoid duplicates by checking ID
    if (!userDrugs.some(d => d.id === drug.id)) {
      const updatedDrugs = [...userDrugs, drug];
      localStorage.setItem(getUserDrugsKey(uid), JSON.stringify(updatedDrugs));
      if (uid) {
        pushClinicianDataToCloud(uid, { customDrugs: updatedDrugs });
      }
    }
  } catch (error) {
    console.error("Could not add user drug to localStorage:", error);
  }
};

/**
 * Fetches all drugs from both the core database and a specific user's custom list.
 * @param uid - The user's unique ID.
 * @returns A promise that resolves with a merged and sorted array of all DrugInfo objects.
 */
export const getAllDrugs = async (uid: string): Promise<DrugInfo[]> => {
  try {
    const coreDrugs = await getLocalDrugDatabase();
    const userDrugs = getUserDrugs(uid);
    const allDrugs = [...coreDrugs, ...userDrugs];
    return allDrugs.sort((a, b) => a.genericName.localeCompare(b.genericName));
  } catch (error) {
    console.error("Could not fetch all drugs:", error);
    return [];
  }
};

/**
 * Finds a single drug by its ID from both core and a specific user's databases.
 * @param id - The ID of the drug to find.
 * @param uid - The user's unique ID.
 * @returns A Promise that resolves with the DrugInfo if found, otherwise null.
 */
export const getDrugById = async (id: string, uid: string): Promise<DrugInfo | null> => {
  try {
    const allDrugs = await getAllDrugs(uid);
    const foundDrug = allDrugs.find(drug => drug.id === id);
    return foundDrug || null;
  } catch (error) {
    console.error("Could not fetch drug by ID:", error);
    return null;
  }
};

/**
 * Searches for a drug by name in both core and a specific user's databases.
 * @param query - The user's search query.
 * @param uid - The user's unique ID.
 * @returns A Promise that resolves with DrugInfo if a match is found, otherwise null.
 */
export const findDrugByName = async (query: string, uid: string): Promise<DrugInfo | null> => {
  try {
    const allDrugs = await getAllDrugs(uid);
    const lowerCaseQuery = query.toLowerCase().trim();
    
    // Simulate slight network delay if needed, or keep immediate
    await new Promise(resolve => setTimeout(resolve, 50));

    const foundDrug = allDrugs.find(
      (drug) =>
        drug.brandName.toLowerCase().includes(lowerCaseQuery) ||
        drug.genericName.toLowerCase().includes(lowerCaseQuery)
    );
    
    return foundDrug || null;
  } catch (error) {
      console.error("Could not search for drug due to loading error:", error);
      return null;
  }
};
